import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./ChangePlan.module.scss";
import PlanCard from "../../components/PlanCard/PlanCard";
import userStore from "../../zustand/user.store";
import { generalConstants, routesConstants } from "../../constants";
import {
  cancelPendingPlanChangeAction,
  changeSubscriptionPlanAction,
  fetchBillingStatusAction,
} from "../../actions/user.actions";
import { userActions } from "../../actions";
import PopUp from "../../components/popUp/PopUp";
import { ICONS } from "../../constants/icons.constants";
import { get } from "lodash";
import TermAndcondition from "../../components/termAndcondition";

const ChangePlan = () => {
  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingStatus, setBillingStatus] = useState(null);
  const [pendingDowngrade, setPendingDowngrade] = useState(null);
  const [isCancellingPending, setIsCancellingPending] = useState(false);
  const [showTnc, setShowTnc] = useState(false);
  const { userOffline, user: userData, setUser } = userStore((state) => ({
    userOffline: state.userOffline,
    user: state.user,
    setUser: state.setUser,
  }));

  // Check if user has no subscription (first-time buyer)
  // NONE = never had subscription, CANCELED = had subscription before (treat as existing user)
  const isFirstTimeBuyer = useMemo(() => {
    const subscriptionStatus = get(billingStatus, "subscription_status", null) || 
                               get(userData, "subscription_status", null);
    // Only NONE or null/undefined is first-time buyer
    // CANCELED users are NOT first-time buyers - they already accepted T&C before
    return subscriptionStatus === "NONE" || subscriptionStatus === null || subscriptionStatus === undefined;
  }, [billingStatus, userData]);

  useEffect(() => {
    const fetchStatus = async () => {
      const res = await fetchBillingStatusAction(userOffline);
      if (res?.success) {
        const status = get(res, "data", null);
        setBillingStatus(status);

        const pendingType = get(status, "pending_plan_type", null);
        const pendingEffectiveAt = get(
          status,
          "pending_plan_effective_at",
          null,
        );
        if (pendingType && pendingEffectiveAt) {
          setPendingDowngrade({
            type: pendingType,
            effectiveAt: pendingEffectiveAt,
          });
        } else {
          setPendingDowngrade(null);
        }
      }
    };
    fetchStatus();
  }, [userOffline]);

  const pricing = useMemo(() => {
    const apiPricing = get(billingStatus, "pricing", null);
    return {
      radio_monthly:
        get(apiPricing, "radio_monthly", null) ??
        generalConstants.PLAN_TYPE_VS_PRICE["RADIO"],
      unlimited_monthly:
        get(apiPricing, "unlimited_monthly", null) ??
        generalConstants.PLAN_TYPE_VS_PRICE["UNLIMITED"],
      trial_days: get(apiPricing, "trial_days", null) ?? 3,
    };
  }, [billingStatus]);

  const handleChangePlan = async () => {
    const res = await changeSubscriptionPlanAction(
      { newPlan: selectedPlan },
      userOffline,
    );
    console.log("res", res);
    if (!res?.success) return;

    // Upgrade: backend returns checkout { url, transactionId }
    const checkout = get(res, "data", null);
    const url = get(res, "data.url", "");
    const transactionId =
      get(res, "data.transactionId", "") || get(res, "data.transaction_id", "");
    console.log("url", url);
    console.log("transactionId", transactionId);
    if (url && transactionId) {
      console.log("navigating to payment");
      try {
        if (checkout) {
          sessionStorage.setItem(
            `billing:checkout:${transactionId}`,
            JSON.stringify(checkout)
          );
        }
      } catch (e) {
        // ignore
      }
      navigate(
        `/payment?paymentUrl=${encodeURIComponent(
          url,
        )}&transactionId=${encodeURIComponent(transactionId)}`,
      );
      // return;
    }

    // Downgrade: backend returns scheduled change fields
    const pendingType = get(res, "data.pending_plan_type", null);
    const pendingEffectiveAt = get(res, "data.pending_plan_effective_at", null);
    if (pendingType && pendingEffectiveAt) {
      console.log("pendingType", pendingType);
      setPendingDowngrade({
        type: pendingType,
        effectiveAt: pendingEffectiveAt,
      });
      // return;
    }

    // Fallback: just go back to profile
    navigate(routesConstants.ROUTES.PROFILE);
    setIsPopupOpen(false);
  };

  const handleSubmit = (type) => {
    setSelectedPlan(type);
    
    // For first-time buyers, show T&C instead of confirmation popup
    if (isFirstTimeBuyer) {
      // Set the plan in user store for T&C component to use
      setUser({
        ...userData,
        plan: { type, price: type === "RADIO" ? pricing.radio_monthly : pricing.unlimited_monthly },
      });
      setShowTnc(true);
    } else {
      // Existing users get confirmation popup
      setIsPopupOpen(true);
    }
  };

  const cancelPendingDowngrade = async () => {
    setIsCancellingPending(true);
    const res = await cancelPendingPlanChangeAction(userOffline);
    if (res?.success) {
      setPendingDowngrade(null);
      const statusRes = await fetchBillingStatusAction(userOffline);
      if (statusRes?.success) setBillingStatus(get(statusRes, "data", null));
    }
    setIsCancellingPending(false);
  };

  return (
    <div className={styles.SelectPlan}>
      {/* Terms and Conditions for first-time buyers */}
      {showTnc && <TermAndcondition setVisible={setShowTnc} visible={showTnc} />}
      
      {/* Confirmation popup for existing subscribers changing plans */}
      <PopUp
        isOpen={isPopupOpen}
        setIsOpen={setIsPopupOpen}
        onSuccess={handleChangePlan}
        title={"Are you sure you want to change your plan?"}
        btnPlaceholder={"Confirm"}
        iconType={ICONS.CHECK_ICON}
      />
      <div className={styles.heading}>
        <h1 className={styles.title1}>Select your Package</h1>
        <p className={styles.title2}>
          Choose the plan that’s right for your business
        </p>
      </div>
      {pendingDowngrade && (
        <div className={styles.heading} style={{ marginTop: 16 }}>
          <p className={styles.title2}>
            Your plan is scheduled to change to{" "}
            <strong>{pendingDowngrade.type}</strong> on{" "}
            <strong>
              {new Date(pendingDowngrade.effectiveAt).toLocaleDateString(
                "en-US",
                { year: "numeric", month: "long", day: "numeric" },
              )}
            </strong>
            .
          </p>
          <p className={styles.title2} style={{ marginTop: 8 }}>
            <button
              type="button"
              onClick={cancelPendingDowngrade}
              disabled={isCancellingPending}
              style={{
                background: "transparent",
                border: "1px solid #6b3eb4",
                color: "#6b3eb4",
                padding: "10px 14px",
                borderRadius: 8,
                cursor: isCancellingPending ? "not-allowed" : "pointer",
              }}
            >
              {isCancellingPending
                ? "Canceling..."
                : "Cancel Scheduled Downgrade"}
            </button>
          </p>
        </div>
      )}
      <div className={styles.cards}>
        <PlanCard
          handleSubmit={() => handleSubmit("RADIO")}
          Title={"Radio"}
          Price={`$${pricing.radio_monthly}`}
          Desc={[
            `${pricing.trial_days} day free trial`,
            "Access the player from a single device",
            "Enjoy over 40,000 tracks, organized into radio stations by style",
            "Listen to more than 25 ad-free radio stations",
            "Schedule radio station playback",
            "Includes a license for business use",
          ]}
        />
        <PlanCard
          isFeatured={true}
          handleSubmit={() => handleSubmit("UNLIMITED")}
          Title={"Unlimited"}
          Price={`$${pricing.unlimited_monthly}`}
          Desc={[
            `Access the player from a single device`,
            `Enjoy over 40,000 tracks, organized into ad-free playlists by style`,
            `Create personal playlists of your favorite music`,
            `Schedule playlists to suit any time of the day`,
            `Mark your favorite songs and block the ones you don't`,
            `Includes a license for business use`,
            `And more...`,
          ]}
        />
        <PlanCard
          handleSubmit={() =>
            window.open("https://4-play.io/contactus", "_blank")
          }
          Title={"Enterprise"}
          Price={"Custom Solution"}
          Desc2={"For chains with 10+ locations"}
          buttonText={"Contact Us"}
          // followUpMessage={
          //   "A representative will contact you within 24 hours for a custom quote"
          // }
          Desc={[
            "Custom solution tailored to your organization",
            "Centralized management of all locations",
            "24/7 advanced technical support",
            "Professional installation and team training",
            "Advanced reporting and analytics",
            "Integration options with existing systems",
            "Special volume pricing",
            "Guaranteed Service Level Agreement (SLA)",
          ]}
        />
      </div>
    </div>
  );
};

export default ChangePlan;
